import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { GOAT_CLEAN_HEADER_DATA, PAGE_DESCRIPTION } from 'goat-clean/enums/goatCleanEnums';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getTranslations } from 'shared/utils/getTranslations';
import { FeatureFlags } from 'featureFlags/constants';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';

const TP = 'pages.goatClean.index';

const GoatClean = ({
  browseKey,
  browseValue,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  isCrawler = false,
}: IProductLandingPage) => {
  const pageDescription = getTranslations(PAGE_DESCRIPTION);
  const goatCleanHeaderData = getTranslations(GOAT_CLEAN_HEADER_DATA);
  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: t(`${TP}.breadcrumbSneakers`, 'Sneakers'), slug: `/sneakers` },
        { name: t(`${TP}.breadcrumbGoatClean`, 'Goat Clean'), slug: `/goat-clean`, isStatic: true },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      displayName={displayName}
      globalPricingId={globalPricingId}
      metadataDescription={pageDescription}
      isCrawler={isCrawler}
      {...goatCleanHeaderData}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const q = QueryString.parse(query);
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnable =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  if (isConsumerSearchEnable) {
    // If consumer search is enabled, we need to fetch the consumer data details
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: { ...q, pageFilters: { conditions: 'goat_clean' } },
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        browseKey: 'group_id',
        browseValue: 'all',
        consumerSearchFilters: { conditions: 'goat_clean' },
        displayName: 'GOAT Clean',
        globalPricingId,
        isCrawler,
        consumerSearchInitialData: JSON.stringify({}),
      },
    };
  }

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      resolvedUrl,
      query,
      filterKey: 'group_id',
      filterValue: 'all',
    });

    const invalidSearch = browseResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        browseKey: 'group_id',
        browseValue: 'all',
        constructorResponse: browseResponse,
        consumerSearchFilters: { conditions: 'goat_clean' },
        displayName: 'GOAT Clean',
        globalPricingId,
        isCrawler,
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default GoatClean;
